import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { debounce } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SettingsService } from '@app/core/settings/services/settings.service';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { ChannelApiService } from 'v2/modules/shared/services/api/channel/channel-api.service';

@Component({
  selector: 'dynamic-filters', // eslint-disable-line @angular-eslint/component-selector
  templateUrl: './dynamic-filters.component.html',
  styleUrls: ['./dynamic-filters.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DynamicFiltersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() configuration :DynamicFilterConfig[];
  @Input() defaultIds :string[] = [];
  @Input() localStorageId :string;
  @Input() defaultValues :Record<string, any> = {};

  @Output() queryChange :EventEmitter<DynamicFilterQuery> =
    new EventEmitter<DynamicFilterQuery>();

  private prependLS :string = 'luminous_dynamicFilter_';
  private filterMap :Record<string, DynamicFilterConfig> = {};
  public filters :DynamicFilterConfig[] = [];
  public filterValues :Record<string, any> = {};

  debounceEmitChange = debounce(this.emitChange, 500);
  urlQueryParams :any = null;
  posChannelControl :UntypedFormControl = new UntypedFormControl('');
  hasPosChanges :boolean = false;
  private onDestroy$ = new Subject();

  constructor(
    private channelApiService :ChannelApiService,
    private cd :ChangeDetectorRef,
    public ft :FeatureToggleService,
    public settingsSvc :SettingsService,
    private activeRoute :ActivatedRoute,
    private router :Router,
  ) {}

  ngOnInit() :void {
    this.buildFilterMap();
    this.loadUrlQueryParams();
    if (!this.restoreFromLocalStorage()) {
      this.reset();
    }
    this.posChannelChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['configuration'] && !changes['configuration'].firstChange) {
      this.buildFilterMap();
      // Rebuild filters if any are using configurations that no longer exist
      this.filters = this.filters.filter(filter => this.filterMap[filter.id]);
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  private buildFilterMap(): void {
    this.filterMap = {};
    if (this.configuration) {
      this.configuration.forEach((filter) => {
        this.filterMap[filter.id] = filter;
      });
    }
  }

  loadUrlQueryParams() :void {
    this.activeRoute.queryParams
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((queryParams) => {
        this.urlQueryParams = queryParams;
      });
  }

  addFilter(filter :DynamicFilterConfig) {
    if (this.filters.every((f) => f.id !== filter.id)) {
      if (filter.type === 'warehouseLocation') {
        this.filterValues[filter.id] = [[], []];
      } else if (filter.type === 'toggle') {
        this.filterValues[filter.id] = false;
        this.emitChange();
      } else {
        this.filterValues[filter.id] = undefined;
      }
      this.filters.push(filter);
      this.updateLocalStorage();
    }
  }

  removeFilter(filter :DynamicFilterConfig) {
    if (this.filterValues[filter.id]) {
      if (filter.id === 'posChannelId') {
        this.posChannelControl.setValue('');
      }
      delete this.filterValues[filter.id];
    } else {
      this.filters = this.filters.filter((f) => f.id !== filter.id);
    }
    window.history.pushState({}, '', window.location.pathname);
    this.emitChange();
  }

  updateAndEmitChange(id :string, event :any) {
    this.filterValues[id] = event?.target?.value || event;
    this.emitChange();
  }

  buildQuery() :Record<string, any> {
    this.updateLocalStorage();
    this.updateUrlWithParams();

    return Object.keys(this.filterValues).reduce((acc, next) => {
      const filter = this.filterMap[next] as DynamicFilterConfig;
      const filterValue = this.filterValues[next];

      // exclusions
      if (!filter) {
        if (filterValue) {
          return {
            ...acc,
            [next]: filterValue ? 1 : 0,
          };
        } else {
          return acc;
        }
      }

      // don't proceed if value is undefined
      if (typeof filterValue === 'undefined' || filterValue === null) {
        return acc;
      }

      // Handle custom field filters
      if (filter.type === 'customField') {
        return this.buildCustomFieldQuery(acc, filter, filterValue);
      }

      // input - if empty, don't return
      if (filter.type === 'input' && !filterValue) {
        return acc;
      }

      // multiInput - if empty, don't return
      if (filter.type === 'multiInput') {
        if (filterValue.length === 0) {
          return acc;
        }
      }

      // select - if empty, don't return
      if (filter.type === 'select' && !filterValue) {
        return acc;
      }

      // toggle - if falsy, don't return
      if (filter.type === 'toggle' && !filterValue) {
        return acc;
      }

      // channels multi=false - if 0, don't return
      if (
        filter.type === 'channels' &&
        !Array.isArray(filterValue) &&
        filterValue === '0'
      ) {
        return acc.toString();
      }

      // array of array
      if (filter.type === 'warehouseLocation') {
        const result = filterValue.reduce((acc2, value, i) => {
          if (value.length) {
            return {
              ...acc2,
              [filter.query[i]]: value.join(','),
            };
          }

          return acc2;
        }, {});

        return { ...acc, ...result };
      }
      // array
      if (Array.isArray(filterValue)) {
        if (!filterValue.length) {
          return acc;
        }

        return {
          ...acc,
          [filter.query as string]: filterValue.join(','),
        };
      }

      // number, string
      return {
        ...acc,
        [filter.query as string]: filterValue,
      };
    }, {});
  }

  private buildCustomFieldQuery(acc: Record<string, any>, filter: any, filterValue: any): Record<string, any> {
    // Handle both old simple value format and new FilterValue format
    let operator = 'eq';
    let value = filterValue;
    
    if (filterValue && typeof filterValue === 'object' && filterValue.operator) {
      operator = filterValue.operator;
      value = filterValue.value;
    }

    // Don't include empty values unless it's a set/notset operator
    if ((operator !== 'set' && operator !== 'notset') && 
        (value === '' || value === null || value === undefined)) {
      return acc;
    }

    // Use the field name from the query (already converted from label to underscore format)
    const fieldName = filter.query;
    
    let paramName: string;
    let paramValue: any;

    // Format parameter name based on operator
    if (operator === 'eq') {
      // Simple equality: custom_fields_[field_name]=[value]
      paramName = `custom_fields_${fieldName}`;
      paramValue = value;
    } else {
      // With operator: custom_fields_[field_name][operator]=[value]
      paramName = `custom_fields_${fieldName}[${operator}]`;
      paramValue = operator === 'set' || operator === 'notset' ? '1' : value;
    }

    return {
      ...acc,
      [paramName]: paramValue,
    };
  }

  updateUrlWithParams() :void {
    const queryParams = {};
    Object.keys(this.filterValues).forEach((queryId) => {
      if (this.filterValues[queryId]) {
        const filter = this.filterMap[queryId] as DynamicFilterConfig;

        if (filter && filter.type === 'date') {
          const dateToArray = this.filterValues[queryId].split('-');
          queryParams[queryId] = dateToArray.join('%');
        } else {
          queryParams[queryId] = this.filterValues[queryId];
        }
      }
    });

    let updatedUrl = window.location.pathname;
    if (Object.keys(queryParams).length > 0) {
      updatedUrl =
        window.location.pathname +
        '?' +
        new URLSearchParams(queryParams).toString();
    }

    history.pushState(queryParams, '', updatedUrl);
  }

  emitChange() :void {
    this.queryChange.emit(this.buildQuery());
  }

  isSelected(id :string) {
    return this.filters.some((f) => f.id === id);
  }

  updateLocalStorage() {
    if (this.localStorageId) {
      localStorage.setItem(
        this.prependLS + this.localStorageId,
        JSON.stringify({ filters: this.filters, values: this.filterValues }),
      );
    }
  }

  restoreFromLocalStorage() :boolean {
    if (this.localStorageId) {
      try {
        const { filters, values } = JSON.parse(
          localStorage.getItem(this.prependLS + this.localStorageId),
        );

        // for both values and filters, remove unmatching ids
        const cleanValues :Record<string, any> = {};
        Object.keys(values).forEach((filterId) => {
          if (this.filterMap[filterId]) {
            cleanValues[filterId] = values[filterId];
          }
        });
        this.filters = filters
          .map((filter) => {
            return this.filterMap[filter.id] || null;
          })
          .filter((a) => a);

        this.loadFromParamsOrStorage(cleanValues);

        return true;
      } catch (e) {
        return false;
      }
    } else {
      return false;
    }
  }

  loadFromParamsOrStorage(cleanValues :Record<string, any> = {}) :void {
    const fromUrlParams = this.formatUrlQueryParams();
    const paramsArray = Object.keys(this.urlQueryParams);
    if (paramsArray.length > 0) {
      this.filterValues = fromUrlParams;
      paramsArray.forEach((queryId) => {
        const filterId =
          this.filters.find((filter) => filter.id === queryId)?.id ?? '';
        if (!filterId && this.filterMap[queryId] !== undefined) {
          this.filters.push(this.filterMap[queryId]);
        }
      });
    } else {
      if (!cleanValues) {
        this.filterValues = this.defaultValues;
      } else {
        this.filterValues = cleanValues;
      }
    }
  }

  formatUrlQueryParams() :any {
    const params = {};
    Object.keys(this.filterMap).forEach((filterId) => {
      const value = this.urlQueryParams[filterId];
      if (value) {
        let formattedValue = null;
        const isArray = value.indexOf(',') > -1;
        const paramDateArray = value.split('%');
        const paramToDate = new Date(paramDateArray);
        const isDate = paramToDate instanceof Date && !isNaN(paramToDate.valueOf());

        if (typeof value === 'string') {
          if (!isArray && this.isInteger(value)) {
            formattedValue = parseInt(value);
          }
          if (!isArray && !this.isInteger(value)) {
            formattedValue = value;
          }
          if (isArray) {
            formattedValue = value
              .split(',')
              .map((item) => (this.isInteger(item) ? parseInt(item) : item));
          }
          if (isDate) {
            formattedValue = paramDateArray.join('-');
          }
        }
        params[filterId] = formattedValue;
      }
    });

    return params;
  }

  setDefaultFilters() {
    this.filters = this.defaultIds.map((id) => this.filterMap[id]);
    window.history.pushState({}, '', window.location.pathname);
  }

  reset() {
    this.filterValues = { ...this.defaultValues };
    this.updateLocalStorage();
    this.setDefaultFilters();
    this.emitChange();
  }

  /**
   * @TODO  This method doesn't feel like it belongs in this generic component.
   */
  private posChannelChanges() :void {
    if (!this.localStorageId) return;

    // get filters if posChannel is available
    const { values } = JSON.parse(
      localStorage.getItem(this.prependLS + this.localStorageId),
    );

    if (values['posChannelId'] !== undefined && values['posChannelId'] !== null) {
      this.channelApiService
        .getById(values['posChannelId'], {})
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((channel) => {
          this.hasPosChanges = true;
          this.posChannelControl.setValue(channel);
        });
    }

    this.posChannelControl.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (this.hasPosChanges) {
          this.hasPosChanges = false;

          return;
        }
        this.filterValues['posChannelId'] = value.id;
        this.emitChange();
      });
  }

  private isInteger(value :string) :boolean {
    return Number.isInteger(Number(value));
  }
}
